import React, { useState, useEffect, useRef } from 'react';
import { X, SwitchCamera, CheckCircle, ChevronRight, Camera, Smartphone, ScanLine, Flashlight, FlashlightOff } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

// localStorage key for onboarding
const ONBOARDING_KEY = 'qrscanner_onboarding_seen';

// Onboarding slides content
const ONBOARDING_SLIDES = [
  {
    icon: Camera,
    title: 'Skannaa QR-koodi',
    description: 'Skannaamalla QR-koodin pääset suoraan kohteen tietoihin. Voit tarkastella ja päivittää kohteen tilaa.'
  },
  {
    icon: Smartphone,
    title: 'Kohdista kamera',
    description: 'Pidä puhelin vakaana ja kohdista QR-koodi skannausruudun sisälle. Skannaus tapahtuu automaattisesti.'
  },
  {
    icon: ScanLine,
    title: 'Kameran käyttöoikeus',
    description: 'Sovellus pyytää lupaa käyttää kameraasi. Hyväksy pyyntö jotta skannaus toimii.'
  }
];

function QRScanner({ mode = 'single', onScan, onClose }) {
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem(ONBOARDING_KEY);
  });
  const [currentSlide, setCurrentSlide] = useState(0);

  // Scanner states
  const [qrHistory, setQRHistory] = useState([]);
  const [scannedDevices, setScannedDevices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [flashlightOn, setFlashlightOn] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = back, 'user' = front

  const html5QrcodeRef = useRef(null);
  const scannerIdRef = useRef('qr-reader-' + Math.random().toString(36).substr(2, 9));
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (html5QrcodeRef.current) {
        html5QrcodeRef.current.stop()
          .catch(err => console.error('Cleanup error:', err))
          .finally(() => {
            if (html5QrcodeRef.current) {
              try {
                html5QrcodeRef.current.clear();
              } catch (e) {
                console.error('Clear error:', e);
              }
              html5QrcodeRef.current = null;
            }
          });
      }
    };
  }, []);

  // Auto-start scanning when onboarding is done
  useEffect(() => {
    if (!showOnboarding && !scanning && !error) {
      startScanning();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnboarding]);

  const logScanToDatabase = async (qrCode, deviceId = null) => {
    try {
      let location = {};
      try {
        if (navigator.geolocation) {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 3000 });
          });
          if (pos) {
            location = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            };
          }
        }
      } catch (geoError) {
        console.log('Location not available:', geoError);
      }

      await fetch('https://www.onetrack.fi/taskmanager/api/devices/log-scan.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qr_code: qrCode,
          device_id: deviceId,
          scan_type: 'in_app',
          ...location
        })
      });
    } catch (error) {
      console.log('Scan logging skipped:', error);
    }
  };

  const startScanning = async () => {
    if (!isMountedRef.current) return;

    try {
      const scannerId = scannerIdRef.current;
      html5QrcodeRef.current = new Html5Qrcode(scannerId);

      await html5QrcodeRef.current.start(
        { facingMode: facingMode },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          if (!isMountedRef.current) return;
          handleQRDetected(decodedText);
          if (mode === 'single') {
            stopScanning();
          }
        },
        () => {}
      );

      if (isMountedRef.current) {
        setScanning(true);
        setError('');
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (isMountedRef.current) {
        setError('Kameran käyttö epäonnistui. Tarkista käyttöoikeudet.');
      }
    }
  };

  const stopScanning = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
        if (isMountedRef.current) {
          setScanning(false);
        }
        try {
          html5QrcodeRef.current.clear();
        } catch (e) {
          console.error('Clear error:', e);
        }
      } catch (err) {
        console.error('Stop error:', err);
        if (isMountedRef.current) {
          setScanning(false);
        }
      } finally {
        html5QrcodeRef.current = null;
      }
    } else {
      if (isMountedRef.current) {
        setScanning(false);
      }
    }
  };

  const handleQRDetected = (qrData) => {
    if (!isMountedRef.current) return;

    console.log('QR Detected:', qrData);
    logScanToDatabase(qrData);

    // Vibrate for feedback
    if (navigator.vibrate) navigator.vibrate(100);

    if (mode === 'single') {
      onScan(qrData);
    } else if (mode === 'batch') {
      if (!scannedDevices.find(d => d.qr_code === qrData)) {
        const newDevice = { qr_code: qrData, timestamp: new Date().toISOString() };
        setScannedDevices(prev => [...prev, newDevice]);
        setQRHistory(prev => [{ data: qrData, timestamp: new Date().toISOString() }, ...prev]);
      }
    }
  };

  const toggleFlashlight = async () => {
    if (html5QrcodeRef.current && scanning) {
      try {
        const track = html5QrcodeRef.current.getRunningTrackCameraCapabilities();
        if (track && track.torchFeature && track.torchFeature().isSupported()) {
          await track.torchFeature().apply(!flashlightOn);
          setFlashlightOn(!flashlightOn);
        }
      } catch (err) {
        console.error('Flashlight toggle error:', err);
      }
    }
  };

  const switchCamera = async () => {
    if (scanning) {
      await stopScanning();
      const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
      setFacingMode(newFacingMode);
      // Small delay before restarting with new camera
      setTimeout(() => {
        if (isMountedRef.current) {
          startScanning();
        }
      }, 300);
    }
  };

  const handleClose = async () => {
    if (html5QrcodeRef.current && scanning) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current.clear();
      } catch (err) {
        console.error('Stop error:', err);
      }
      html5QrcodeRef.current = null;
    }
    onClose();
  };

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setShowOnboarding(false);
  };

  const nextSlide = () => {
    if (currentSlide < ONBOARDING_SLIDES.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      completeOnboarding();
    }
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  // Onboarding Screen
  if (showOnboarding) {
    const slide = ONBOARDING_SLIDES[currentSlide];
    const Icon = slide.icon;
    const isLastSlide = currentSlide === ONBOARDING_SLIDES.length - 1;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        zIndex: 4000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '2rem 1.5rem'
      }}>
        {/* Skip button */}
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={skipOnboarding}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            Ohita
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          maxWidth: '320px'
        }}>
          {/* Title */}
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: 'white',
            marginBottom: '3rem'
          }}>
            {slide.title}
          </h1>

          {/* Icon */}
          <div style={{
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'rgba(255, 235, 59, 0.1)',
            border: '2px solid rgba(255, 235, 59, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '3rem'
          }}>
            <Icon size={80} color="#FFEB3B" strokeWidth={1.5} />
          </div>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            lineHeight: '1.6',
            color: '#cbd5e1',
            margin: 0
          }}>
            {slide.description}
          </p>
        </div>

        {/* Bottom section */}
        <div style={{
          width: '100%',
          maxWidth: '320px'
        }}>
          {/* Pagination dots */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '1.5rem'
          }}>
            {ONBOARDING_SLIDES.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: idx === currentSlide ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: idx === currentSlide ? '#FFEB3B' : '#475569',
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </div>

          {/* Button */}
          <button
            onClick={nextSlide}
            style={{
              width: '100%',
              padding: '1rem',
              background: '#FFEB3B',
              color: '#0f172a',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {isLastSlide ? 'Aloita skannaus' : 'Seuraava'}
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  // Scanner Screen
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#000',
      zIndex: 4000
    }}>
      {/* Camera view container */}
      <div
        id={scannerIdRef.current}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden'
        }}
      />

      {/* Overlay UI */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Top bar */}
        <div style={{
          padding: '1rem 1.5rem',
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pointerEvents: 'auto'
        }}>
          {/* Close button */}
          <button
            onClick={handleClose}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            <X size={24} />
          </button>

          {/* Title */}
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'white',
            margin: 0,
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            paddingTop: '0.5rem'
          }}>
            Skannaa QR-koodi
          </h2>

          {/* Spacer for alignment */}
          <div style={{ width: '44px' }} />
        </div>

        {/* Center - Scanning frame */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Scanning frame corners */}
          <div style={{
            width: '260px',
            height: '260px',
            position: 'relative'
          }}>
            {/* Top-left corner */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '50px',
              height: '50px',
              borderTop: '4px solid white',
              borderLeft: '4px solid white',
              borderTopLeftRadius: '12px'
            }} />
            {/* Top-right corner */}
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '50px',
              height: '50px',
              borderTop: '4px solid white',
              borderRight: '4px solid white',
              borderTopRightRadius: '12px'
            }} />
            {/* Bottom-left corner */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '50px',
              height: '50px',
              borderBottom: '4px solid white',
              borderLeft: '4px solid white',
              borderBottomLeftRadius: '12px'
            }} />
            {/* Bottom-right corner */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '50px',
              height: '50px',
              borderBottom: '4px solid white',
              borderRight: '4px solid white',
              borderBottomRightRadius: '12px'
            }} />

            {/* Scanning line animation */}
            {scanning && (
              <div style={{
                position: 'absolute',
                top: '10%',
                left: '10%',
                right: '10%',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #FFEB3B, transparent)',
                animation: 'scanLine 2s ease-in-out infinite',
                boxShadow: '0 0 10px #FFEB3B'
              }} />
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '1rem',
            margin: '0 1.5rem',
            background: 'rgba(239, 68, 68, 0.9)',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center',
            fontSize: '14px',
            pointerEvents: 'auto'
          }}>
            {error}
            <button
              onClick={startScanning}
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#ef4444',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'block',
                width: '100%'
              }}
            >
              Yritä uudelleen
            </button>
          </div>
        )}

        {/* Batch mode - scanned count */}
        {mode === 'batch' && scannedDevices.length > 0 && (
          <div style={{
            margin: '0 1.5rem 1rem',
            padding: '1rem',
            background: 'rgba(16, 185, 129, 0.9)',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center',
            pointerEvents: 'auto'
          }}>
            <div style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
              <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
              Skannattu: {scannedDevices.length} kohdetta
            </div>
            <button
              onClick={() => {
                stopScanning();
                setTimeout(() => onScan(scannedDevices), 100);
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#10b981',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Valmis ({scannedDevices.length})
            </button>
          </div>
        )}

        {/* Bottom bar */}
        <div style={{
          padding: '1.5rem',
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
          pointerEvents: 'auto'
        }}>
          {/* Control buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '200px',
            margin: '0 auto'
          }}>
            {/* Flashlight */}
            <button
              onClick={toggleFlashlight}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: flashlightOn ? '#FFEB3B' : 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: flashlightOn ? '#0f172a' : 'white'
              }}
            >
              {flashlightOn ? <FlashlightOff size={24} /> : <Flashlight size={24} />}
            </button>

            {/* Camera switch */}
            <button
              onClick={switchCamera}
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <SwitchCamera size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes scanLine {
          0%, 100% {
            top: 10%;
          }
          50% {
            top: 80%;
          }
        }

        /* Force video to fill entire screen on all devices */
        #${scannerIdRef.current} {
          width: 100vw !important;
          height: 100vh !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
        }

        #${scannerIdRef.current} video {
          object-fit: cover !important;
          width: 100vw !important;
          height: 100vh !important;
          min-width: 100vw !important;
          min-height: 100vh !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
        }

        #${scannerIdRef.current} > div {
          border: none !important;
          width: 100% !important;
          height: 100% !important;
        }

        /* Hide the default scanning region */
        #${scannerIdRef.current} #qr-shaded-region {
          display: none !important;
        }

        /* Hide any internal html5-qrcode elements */
        #${scannerIdRef.current} img {
          display: none !important;
        }
      `}</style>
    </div>
  );
}

export default QRScanner;
